import React, { useEffect, useRef, useState } from 'react';

import { aiAssistantQuery } from '~/src/dash/api/console-api';
import { ButtonV2 } from '~/src/dash/components/buttons/ButtonV2';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { borderRadius, Div, Panel, Panels, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { routeName } from '~/src/dash/utils/routes';
import { getSessionPublisherId } from '~/src/dash/utils/session';
import { useBaseStore } from '~/src/dash/views/base/contexts';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  query?: string;
}

export const AdVisor = (): React.JSX.Element => {
  const { location, session, user } = useBaseStore();
  const { pathname } = location;
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(new Set());

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatResponse = (
    data: string | Array<{ [key: string]: string }>,
    rowCount: number,
  ): string => {
    if (typeof data === 'string') {
      return data;
    }
    if (Array.isArray(data)) {
      const formattedData = data
        .map((item) => {
          const jsonString = JSON.stringify(item, null, 2);
          // remove the opening { and closing } and their associated newlines
          return jsonString.replace(/^\{\n/, '').replace(/\n\}$/, '').trim();
        })
        .join('\n');
      if (data.length < rowCount) {
        return `Here are the sample result:\n${formattedData}`;
      }
      return formattedData;
    }
    if (typeof data === 'object') {
      return JSON.stringify(data, null, 2);
    }
    return String(data);
  };

  const handleSend = async (): Promise<void> => {
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const userQuestion = inputValue.trim();
    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content: userQuestion }]);
    setIsLoading(true);

    try {
      const [response, error] = await aiAssistantQuery(
        userQuestion,
        getSessionPublisherId(session),
      );

      if (error) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `Error: ${
              error.message || 'An error occurred while processing your request.'
            }`,
          },
        ]);
      } else if (response) {
        if (response.success === false) {
          setMessages((prev: Message[]) => [
            ...prev,
            {
              role: 'assistant',
              content: `Error: ${response.error || 'An error occurred.'}`,
            },
          ]);
        } else {
          const formattedResponse = formatResponse(response.data, response.rowCount);
          setMessages((prev: Message[]) => [
            ...prev,
            {
              role: 'assistant',
              content: formattedResponse,
              query: response.query,
            },
          ]);
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Error: ${err instanceof Error ? err.message : 'An unexpected error occurred.'}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const toggleQuery = (index: number): void => {
    setExpandedQueries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Panels maxWidth vertical relative minWidthMain>
      <Panel
        largeHorizontalPadding
        verticalPadding
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <ModelTopBar
          title={routeName(pathname)}
          session={session}
          user={user}
          publisher={session.publisher}
          currentPath={pathname}
          disableTitlePaddingLeft
        />
      </Panel>
      <Panel scroll style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Panels maxWidth vertical relative minWidthMain largePadding>
          <Panel style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Rows>
              <Row noVerticalPadding>
                <Text header>Publica's AI Assistant</Text>
              </Row>
              <Row noVerticalPadding>
                <Text small muted>
                  Ask questions about your platform data and get AI-powered answers.
                </Text>
              </Row>
            </Rows>
            <Panel
              highlighted
              basePadding
              marginBottom={spacing.large}
              style={{
                minHeight: '400px',
                maxHeight: '600px',
              }}
              scroll
            >
              {messages.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    paddingTop: spacing.extraLarge,
                    color: publicaColors.gray,
                  }}
                >
                  <Text>Start a conversation by asking a question below.</Text>
                </div>
              )}
              {messages.map((message, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: spacing.medium,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '80%',
                      padding: spacing.medium,
                      borderRadius: '8px',
                      backgroundColor:
                        message.role === 'user'
                          ? publicaColors.lightBlue
                          : publicaColors.lighterGreen,
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '14px',
                        lineHeight: '1.5',
                        color:
                          message.role === 'user'
                            ? publicaColors.white
                            : publicaColors.veryDarkGray,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {message.content}
                    </Text>
                    {message.role === 'assistant' && message.query && (
                      <div
                        style={{
                          marginTop: spacing.medium,
                          paddingTop: spacing.medium,
                          borderTop: `1px solid ${publicaColors.lightGray}`,
                        }}
                      >
                        <div
                          onClick={() => toggleQuery(index)}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.small,
                            userSelect: 'none',
                            color: publicaColors.blue,
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          <span>{expandedQueries.has(index) ? '▼' : '▶'}</span>
                          <span>View SQL Query</span>
                        </div>
                        {expandedQueries.has(index) && (
                          <div
                            style={{
                              marginTop: spacing.small,
                              padding: spacing.small,
                              backgroundColor: publicaColors.white,
                              borderRadius: '4px',
                              border: `1px solid ${publicaColors.lightGray}`,
                              wordBreak: 'break-word',
                              maxHeight: '300px',
                              overflowY: 'auto',
                            }}
                          >
                            <Text
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                color: publicaColors.veryDarkGray,
                                whiteSpace: 'pre-wrap',
                                margin: 0,
                              }}
                            >
                              {message.query}
                            </Text>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div
                  style={{
                    marginBottom: spacing.medium,
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      padding: spacing.medium,
                      borderRadius: '8px',
                      backgroundColor: publicaColors.white,
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Text small muted>
                      Thinking...
                    </Text>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Panel>

            <div
              style={{
                display: 'flex',
                gap: spacing.medium,
                alignItems: 'flex-end',
              }}
            >
              <Div style={{ flex: 1 }}>
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder='Type your question here... (Press Enter to send, Shift+Enter for new line)'
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    maxHeight: '120px',
                    padding: '8px 12px',
                    borderRadius: borderRadius.small,
                    border: `0.5px solid ${publicaColors.lightGray}`,
                    backgroundColor: publicaColors.white,
                    color: publicaColors.veryDarkGray,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: '14px',
                    resize: 'vertical' as const,
                    overflowY: 'auto' as const,
                    boxShadow: 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
                  }}
                />
              </Div>
              <ButtonV2
                onClick={() => void handleSend()}
                disabled={!inputValue.trim() || isLoading}
              >
                Send
              </ButtonV2>
            </div>
          </Panel>
        </Panels>
      </Panel>
    </Panels>
  );
};
